import { qualityOf } from '../../edge/quality'
import { todayHighLow } from '../lib/forecast'
import type { WeatherReport } from '../../shared/contract'

/** Refinement must not erase a section while its own upstream is still pending. */
export function mergeProgress(previous: WeatherReport | undefined, next: WeatherReport): WeatherReport {
  if (!previous || !samePlace(previous, next)) return next
  const loading = (...ids: string[]) => next.sources.some((s) => ids.includes(s.id) && /^(?:Radar )?still loading/i.test(s.detail))
  const merged = { ...next }
  if (loading('openmeteo_models') && previous.hourly.length) {
    merged.hourly = previous.hourly
    merged.daily = previous.daily
    merged.fusion = previous.fusion
    merged.summaries = previous.summaries
  }
  if (loading('nws_observations') && next.current.temperature_c == null) merged.current = previous.current
  if (loading('iem_radar', 'openmeteo_minutely')) merged.minutely = previous.minutely
  if (loading('nws_alerts')) merged.alerts = previous.alerts
  if (!next.averages) merged.averages = loading('normals') ? previous.averages : retainNormals(previous, merged).averages
  if (loading('openmeteo_air', 'airnow') && !next.air_quality) merged.air_quality = previous.air_quality
  if (loading('coops') && !next.tides) merged.tides = previous.tides
  return retainSections(previous, merged)
}

/** Normals are immutable reference data. A quota-limited refinement must not
 * erase a valid same-day edge/cached normal, or persist a less complete warm view. */
export function retainNormals(previous: WeatherReport | undefined, next: WeatherReport): WeatherReport {
  if (!previous?.averages || next.averages || previous.daily[0]?.date !== next.daily[0]?.date ||
    Math.abs(previous.location.latitude - next.location.latitude) > 0.011 ||
    Math.abs(previous.location.longitude - next.location.longitude) > 0.011) return next
  const oldSource = previous.sources.find((s) => s.id === 'normals')
  const reason = next.sources.find((s) => s.id === 'normals')?.detail ?? 'refresh unavailable'
  const a = previous.averages
  const { high, low } = todayHighLow(next)
  const departure = (v: number | null | undefined, normal: number) => v == null ? null : Math.round((v - normal) * 10) / 10
  const retained = oldSource && { ...oldSource, status: 'partial' as const, detail: `Cached reference normals retained; ${reason}` }
  return { ...next,
    averages: { ...a, high_departure_c: departure(high, a.normal_high_c), low_departure_c: departure(low, a.normal_low_c) },
    sources: retained ? [...next.sources.filter((s) => s.id !== 'normals'), retained] : next.sources,
    attribution: [...new Set([...next.attribution, ...previous.attribution])],
  }
}

/** A failed core refresh is not a fidelity upgrade. Keep a recent complete forecast,
 * while still accepting fresh alerts and independent AQI/tide/outlook updates. */
export function retainHealthyForecast(previous: WeatherReport | undefined, next: WeatherReport, now = Date.now()): WeatherReport {
  if (!previous || previous.sources.some((s) => /^(?:Radar )?still loading/i.test(s.detail)) ||
    qualityOf(previous).tier !== 'full' || qualityOf(next).tier !== 'degraded' ||
    now - Date.parse(previous.generated_at) > 15 * 60_000 ||
    previous.daily[0]?.date !== next.daily[0]?.date ||
    Math.abs(previous.location.latitude - next.location.latitude) > 0.011 ||
    Math.abs(previous.location.longitude - next.location.longitude) > 0.011) return next
  const updated = new Set<string>()
  const ready = (id: string) => {
    const s = next.sources.find((s) => s.id === id)
    if (!s || !['ok', 'partial'].includes(s.status)) return false
    updated.add(id)
    return true
  }
  const alerts = ready('nws_alerts') ? next.alerts : previous.alerts
  const severe = { spc: ready('spc') ? next.severe.spc : previous.severe.spc, tropical: ready('nhc') ? next.severe.tropical : previous.severe.tropical }
  if (next.air_quality) { ready('airnow'); ready('openmeteo_air') }
  if (next.tides) ready('coops')
  return { ...previous, alerts, severe, averages: next.averages ?? previous.averages,
    air_quality: next.air_quality ?? previous.air_quality, tides: next.tides ?? previous.tides,
    sources: previous.sources.map((s) => updated.has(s.id) ? next.sources.find((n) => n.id === s.id)! : s),
    fusion: { ...previous.fusion, notes: [...new Set([...previous.fusion.notes, 'Browser refresh lacked core inputs; retaining the recent complete forecast.'])] },
    attribution: [...new Set([...previous.attribution, ...next.attribution])],
  }
}

function samePlace(a: WeatherReport, b: WeatherReport): boolean {
  return Math.abs(a.location.latitude - b.location.latitude) <= 0.011 &&
    Math.abs(a.location.longitude - b.location.longitude) <= 0.011
}

/** Fill missing optional sections only; never replace a newer forecast with an edge snapshot. */
export function retainSections(previous: WeatherReport | undefined, next: WeatherReport): WeatherReport {
  if (!previous || !samePlace(previous, next)) return next
  const merged = retainNormals(previous, next)
  if (previous.daily[0]?.date !== next.daily[0]?.date) return merged
  return { ...merged, air_quality: next.air_quality ?? previous.air_quality,
    tides: next.tides ?? previous.tides,
    current: next.current.uv_index == null && previous.current.uv_index != null
      ? { ...next.current, uv_index: previous.current.uv_index } : next.current,
  }
}

/** Absence is pending until the relevant sources have actually answered. */
export function sectionPending(report: WeatherReport, ids: string[], fetching = false): boolean {
  if (fetching) return true
  const sources = report.sources.filter(s => ids.includes(s.id))
  return !sources.length || sources.some(s => /still loading/i.test(s.detail))
}
